import { useMemo, ReactElement } from 'react';

import { paths } from 'src/routes/paths';

import { useTranslate } from 'src/locales';
import { useAuthContext } from 'src/auth/hooks';
import { isAllowedTo } from 'src/services/util';
import { Permission, PrivilegeType } from 'src/services/role';

// ----------------------------------------------------------------------

const icon = (name: string) => (
  <img src={`/assets/icons/navbar/${name}.png`} alt={name} style={{ width: 16, height: 16 }} />
);

const ICONS = {
  user: icon('ic_user'),
  dashboard: icon('ic_dashboard'),
  subject: icon('ic_subject'),
  department: icon('ic_department'),
  dimension: icon('ic_dimension'),
  system: icon('ic_system'),
  analysis: icon('ic_analysis'),
  sales: icon('ic_sales'),
  proposal: icon('ic_user'),
};

interface NavItem {
  title: string;
  path: string;
  icon: ReactElement;
  children?: {
    title: string;
    priviledgeKeys?: PrivilegeType[];
    path: string;
    associativePath?: string | string[];
  }[];
  priviledgeKeys?: PrivilegeType[];
  associativePath?: string | string[];
}

export const roleList: NavItem[] = [
  {
    title: '驾驶舱',
    path: paths.dashboard.root,
    icon: ICONS.dashboard,
    priviledgeKeys: [PrivilegeType.ADMIN, PrivilegeType.DASHBOARD],
  },
  {
    title: '目标与项目',
    path: paths.dashboard.objectivesManagement.root,
    icon: ICONS.subject,
    children: [
      {
        title: '目标拆解',
        path: paths.dashboard.objectivesManagement.planManagement.root,
        priviledgeKeys: [PrivilegeType.PLAN],
        associativePath: [
          paths.dashboard.objectivesManagement.planManagement.new,
          `${paths.dashboard.objectivesManagement.planManagement.root}/:id`,
          `${paths.dashboard.objectivesManagement.planManagement.root}/:id/edit`,
        ],
      },
      {
        title: '项目(策略)管理',
        path: paths.dashboard.objectivesManagement.projectManagement.root,
        priviledgeKeys: [PrivilegeType.PROJECT],
        associativePath: [
          paths.dashboard.objectivesManagement.projectManagement.new,
          `${paths.dashboard.objectivesManagement.projectManagement.root}/:id`,
          `${paths.dashboard.objectivesManagement.projectManagement.root}/:id/edit`,
        ],
      },
      {
        title: '年度计划表',
        path: paths.dashboard.objectivesManagement.businessPlanReport.root,
        priviledgeKeys: [PrivilegeType.PLAN],
      },
      {
        title: '核心指标年度计划表',
        path: paths.dashboard.objectivesManagement.kpiReport.root,
        priviledgeKeys: [PrivilegeType.PLAN],
      },
    ],
  },
  {
    title: '在线交易',
    path: paths.dashboard.onlineTrading.root,
    icon: ICONS.sales,
    children: [
      {
        title: '内部交易',
        path: paths.dashboard.onlineTrading.internalTrading.root,
        priviledgeKeys: [PrivilegeType.INTERNAL_TRADE],
      },
      {
        title: '订单数据流水',
        path: paths.dashboard.onlineTrading.operationalStatementTransaction.root,
        priviledgeKeys: [PrivilegeType.OPERATIONAL_STATEMENT_TRANSACTION],
      },
    ],
  },

  {
    title: '经营分析',
    path: paths.dashboard.analysis.root,
    icon: ICONS.analysis,
    children: [
      {
        title: '报表收入明细',
        path: paths.dashboard.analysis.statementIncomeList,
        priviledgeKeys: [PrivilegeType.INCOME_ANALYSIS],
      },
      {
        title: '报表支出明细',
        path: paths.dashboard.analysis.statementExpenseList,
        priviledgeKeys: [PrivilegeType.EXPENSE_ANALYSIS],
      },
      {
        title: '目标差异分析',
        path: paths.dashboard.analysis.targetDifferenceList,
        priviledgeKeys: [PrivilegeType.VARIANCE_ANALYSIS],
        associativePath: [
          paths.dashboard.analysis.targetDifference.list,
          `${paths.dashboard.analysis.targetDifference.root}/:id`,
          `${paths.dashboard.analysis.targetDifference.root}/:id/edit`,
        ],
      },
      {
        title: '经营会计报表',
        path: paths.dashboard.analysis.analysisReportList,
        priviledgeKeys: [PrivilegeType.INCOME_ANALYSIS],
      },
    ],
  },
  {
    title: '改善项目',
    path: paths.dashboard.improvementProposal.root,
    icon: ICONS.proposal,
    children: [
      {
        title: '改善提案',
        path: paths.dashboard.improvementProposal.root,
        priviledgeKeys: [PrivilegeType.PROPOSAL],
      },
      {
        title: '行动计划管理',
        path: paths.dashboard.improvementProposal.actionPlanManagement.root,
        priviledgeKeys: [PrivilegeType.ACTION_PLAN],
        associativePath: [
          paths.dashboard.improvementProposal.actionPlanManagement.new,
          `${paths.dashboard.improvementProposal.actionPlanManagement.root}/:id`,
          `${paths.dashboard.improvementProposal.actionPlanManagement.root}/:id/edit`,
        ],
      },
      {
        title: '执行率',
        path: paths.dashboard.improvementProposal.executionRate.root,
        priviledgeKeys: [PrivilegeType.PLAN],
      },
      {
        title: '支援配合',
        path: paths.dashboard.improvementProposal.cooperation.root,
        priviledgeKeys: [PrivilegeType.SUPPORT_REQUEST],
      },
    ],
  },
  {
    title: '系统管理',
    path: paths.dashboard.systemManagement.root,
    icon: ICONS.system,
    children: [
      {
        title: '考核维度',
        path: paths.dashboard.systemManagement.assessmentDimension.root,
        priviledgeKeys: [PrivilegeType.ASSESSMENT_DIMENSION],
      },
      {
        title: '考核科目',
        path: paths.dashboard.systemManagement.subject.root,
        priviledgeKeys: [PrivilegeType.SUBJECT],
      },
      {
        title: '汇率管理',
        path: paths.dashboard.systemManagement.currencyConversion.root,
        priviledgeKeys: [PrivilegeType.CURRENCY_CONVERSION],
      },
      {
        title: '报表模版',
        path: paths.dashboard.systemManagement.reportTemplate.root,
        priviledgeKeys: [PrivilegeType.TEMPLATE],
      },
      {
        title: '交易类型管理',
        path: paths.dashboard.systemManagement.tradeCategory.root,
        priviledgeKeys: [PrivilegeType.TRADE_CATEGORY],
      },
    ],
  },
];

// ----------------------------------------------------------------------
// 其他icon在https://icon-sets.iconify.design/ph/上面找的
const getManageItems = (permissions?: Permission) => {
  const list: NavItem[] = [];
  if (!permissions) {
    return list;
  }

  roleList.forEach((item) => {
    if (item?.priviledgeKeys && !!item.priviledgeKeys.find((v) => isAllowedTo(v, permissions))) {
      list.push(item);
    } else if (item?.children?.length && !item?.priviledgeKeys) {
      const arr = item.children.filter((childrenItem) =>
        childrenItem.priviledgeKeys
          ? !!childrenItem.priviledgeKeys.find((v) => isAllowedTo(v, permissions))
          : !childrenItem.priviledgeKeys
      );
      if (arr?.length) {
        list.push({
          ...item,
          children: arr,
        });
      }
    }
  });
  return list;
};

export function useNavData() {
  const { t } = useTranslate();
  const { user } = useAuthContext();
  const list = getManageItems(user?.permissions);

  const data = useMemo(
    () => [
      {
        subheader: '',
        items: [...list],
      },
    ],
    [t, list]
  );

  return data;
}
